<template>
  <div class="v2-input-wrapper">
    <label
      v-if="label"
      class="v2-input__label"
    >{{ label }}</label>
    <input
      class="v2-input"
      :class="{ 'v2-input--error': error }"
      :value="value"
      :type="type"
      :step="step"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      v-on="inputListeners"
    >
    <small
      v-if="error"
      class="v2-input__error"
    >{{ error }}</small>
  </div>
</template>

<script>
export default {
  name: 'V2Input',
  props: {
    value: { type: [String, Number], default: '' },
    placeholder: { type: String, default: '' },
    type: { type: String, default: 'text' },
    // Forwarded to the native input. `null` leaves the attribute unset so
    // number inputs keep their default (integer) step; pass "any" to allow
    // decimals.
    step: { type: [String, Number], default: null },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    error: { type: String, default: '' },
    label: { type: String, default: '' },
  },
  computed: {
    inputListeners() {
      return {
        ...this.$listeners,
        input: e => this.$emit('input', e.target.value),
        blur: e => this.$emit('blur', e),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-1);
}

.v2-input__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v2-foreground);
}

// Glass input — surface-2 + blur-sm + glass-stroke. Earlier the team
// reverted to opaque white because the recessed-pocket recipe (18% black
// + inset shadow) felt neumorphic at form density. This recipe is much
// lighter (no inset, no dark tint) so it matches the rest of the v2
// glass surfaces without the neumorphic feel.
.v2-input {
  width: 100%;
  min-height: 38px;
  padding: var(--v2-space-2) var(--v2-space-3);
  font-family: var(--v2-font-sans);
  font-size: 0.875rem;
  color: var(--v2-foreground);
  // No `backdrop-filter` — inputs render many at a time on form-heavy
  // pages. Translucent fill + glass-stroke read as glass over the page
  // mesh substrate without per-input GPU blur.
  background: var(--v2-glass-surface-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-md);
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--v2-muted-foreground);
  }

  &:hover:not(:disabled):not(:focus):not([readonly]) {
    border-color: var(--v2-glass-stroke-strong);
  }

  &:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
  }

  &:disabled,
  &[readonly] {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--v2-muted);
  }

  &--error {
    border-color: var(--v2-destructive);

    &:focus {
      box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.25);
    }
  }
}

.v2-input__error {
  font-size: 0.75rem;
  color: var(--v2-destructive);
}
</style>
